import React, {
  useEffect, useState, useRef, Fragment, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Session from '@nmx/utils/dist/utilities/frontend/session';
import { withTheme } from 'styled-components';
import _debounce from 'lodash/debounce';
import { Container, Row, Col } from '../../base/Grid';
import Button from '../../base/Button';
import Link from '../../base/Link';
import Icon from '../../base/Icon';

// styles
import {
  InformationalBanner, ContainerDefault, DismissButton, DataSeparator, DataTextContainer, StartAdornment, Styles,
} from './styles'; // TODO: move styles to Component
import './informationalBanner.scss';

import DynamicConfig from '../../utils/dynamicConfig';

export const BannerModule = ({
  'data-banner-id': bannerId = 'nmx-informational-banner',
  'data-app-name': appName,
  'data-dynamic-config-base-url': dynamicConfigBaseUrl,
  'data-static-config': staticConfig,
}) => {
  const configName = (appName !== 'nmx-static'
  && appName !== 'nmx-static-fa-profile-pages'
  && appName !== 'nmx-static-articles')
    ? 'default'
    : appName;

  // ref is used because event listeners are scoping out the value
  // the resize event calls handleMobileBannerDisplay which would show an empty bannerConfig if this was a state instead of a ref
  const bannerConfig = useRef();
  const bannerEl = useRef(null);
  const [retrievedBannerConfig, setRetrievedBannerConfig] = useState(false); // triggers render to show banner
  const [bannerDismissed, setBannerDismissed] = useState(false); // when banner is dismissed trigger function to resize stuff
  const [drawerStatus, setDrawerStatus] = useState(false);

  // when the banner is initially rendered, the bannerEl ref shows the wrong height.
  // when we set this to true it re renders the component and seems to fix that issue. probably a better way to do this.
  const [initialBannerRender, setInitialBannerRender] = useState(false);

  const styles = Styles();

  const headerLogoContainer = document.getElementById('header-logo-heading');
  const mobileTriggerOpen = document.getElementById('nav-toggle');
  const nmLoginButton = document.querySelector('.nm-login-button');
  const mobileNav = document.getElementById('nmx-mobile-nav-container');
  const headerContainer = document.getElementById('nmx-header');

  // constants
  const DEFAULT_BG_COLOR = '#F7FAFC';
  const DEFAULT_HIDE_DISMISS_BUTTON = false;
  const DISMISSED_SESSION_KEY = `${configName}-dismiss-banner`;
  const LISTENER_THROTTLE = 100;

  const mmCheck = () => window.matchMedia('(min-width: 769px)').matches;

  function bannerIsVisible() {
    return bannerEl.current
      && bannerConfig.current
      && bannerConfig.current.isActive === true
      && !bannerDismissed;
  }

  function handleMobileBannerDisplay() {
    if (bannerIsVisible() === true) {
      if (mmCheck()) {
        // desktop styles // reset
        headerLogoContainer.style.top = mobileNav.style.top = 0;
        nmLoginButton.style.top = mobileTriggerOpen.style.top = null; // remove inline style
        headerContainer.style.height = 'auto';
      } else {
        // mobile styles // adjust to height of informational banner
        nmLoginButton.style.top = mobileTriggerOpen.style.top = `${bannerEl.current.clientHeight}px`;
        if (headerContainer.classList.contains('is-logo-only')) {
          mobileNav.style.top = headerContainer.style.height = `${bannerEl.current.clientHeight}px`; // banner height
        } else {
          mobileNav.style.top = headerContainer.style.height = `${headerLogoContainer.clientHeight + bannerEl.current.clientHeight}px`; // height of mobile logo container + banner height
          if (initialBannerRender) {
            headerLogoContainer.style.top = `${bannerEl.current.clientHeight}px`;
          }
        }
        // NOTE: only moving piece not handled here for banner sizing, is #header-logo-heading (headerLogoContainer), handled in nav.header.js
      }
    } else {
      // remove any immediate RE:banner styles
      headerLogoContainer.style.top = mobileNav.style.top = mobileTriggerOpen.style.top = nmLoginButton.style.top = null;
      headerContainer.style.height = 'auto';
    }
  }

  // do this on mount
  useEffect(() => {
    // used by storybook
    if (staticConfig) {
      bannerConfig.current = staticConfig.informationalBanner;
      setRetrievedBannerConfig(true); // TODO: consolidate
      if (Session.get(DISMISSED_SESSION_KEY) !== undefined) {
        setBannerDismissed(Session.get(DISMISSED_SESSION_KEY));
      }
      headerContainer.classList.add('nmx-informational-banner-active');
      return;
    }
    if (window.navigator.userAgent.match(/MSIE|Trident/) !== null) { // check for <= IE11, needs to be !== for prod
      bannerConfig.current = {
        data: {
          text: [
            'We noticed you are using an older version of Internet Explorer that will soon be unsupported.',
            // 'Please upgrade your browser to the next recommended version.',
          ],
          buttonCtaText: 'Upgrade Browser',
          buttonCtaUrl: 'https://microsoftedgewelcome.microsoft.com',
          ctaUrlNewWindow: true,
        },
        hideDismissButton: false,
        isActive: true,
        startAdornment: 'more-info-stroke',
        variant: 'cta-button',
      };
      setRetrievedBannerConfig(true); // TODO: consolidate
      if (Session.get(DISMISSED_SESSION_KEY) !== undefined) {
        setBannerDismissed(Session.get(DISMISSED_SESSION_KEY));
      }
      headerContainer.classList.add('nmx-informational-banner-active');
      return;
    }

    const dynamicConfigUrl = `${dynamicConfigBaseUrl}/${configName}.json`;
    const dynamicConfigSessionKey = `${configName}-dynamic-config`;

    DynamicConfig(dynamicConfigUrl, dynamicConfigSessionKey).then((dynamicConfig) => {
      bannerConfig.current = dynamicConfig.banner; // TODO: informationalBanner
      if (Session.get(DISMISSED_SESSION_KEY) !== undefined) {
        setBannerDismissed(Session.get(DISMISSED_SESSION_KEY));
      }
      setRetrievedBannerConfig(true);
    });
    headerContainer.classList.add('nmx-informational-banner-active');
  }, []);

  // do this when bannerDismissed is modified
  useEffect(() => {
    // listen to changes in bannerConfig
    handleMobileBannerDisplay();
  }, [bannerDismissed]);

  // do this on mount and unmount
  // having event listeners causes all kinds of fun stuff which is why bannerConfig uses the useRef hook instead of useState
  // https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
  useEffect(() => {
    // add resize listener on Mount
    window.addEventListener('resize', _debounce(handleMobileBannerDisplay, LISTENER_THROTTLE));
    return () => {
      // remove resize listener on Unmount
      window.removeEventListener('resize', handleMobileBannerDisplay);
    };
  }, []);

  // do this on every render
  useEffect(() => {
    // when element is fully rendered
    if (bannerIsVisible() && !mmCheck()) {
      handleMobileBannerDisplay();
    }
  });

  const toggleDrawer = () => {
    // ensure mobile header elements are where they're supposed to be
    // add a 50ms delay to this re-render
    // TODO: figure out a better way to handle drawer open re-position, for some reason, height is not exact and needs to be force-updated
    setDrawerStatus(!drawerStatus);
    const intervalId = setTimeout(handleMobileBannerDisplay, 50);
  };

  const handleCtaClick = () => { // TODO: probably can consolidate these with some config adjustments // TODO: build into Link Component?
    console.log('handleCtaClick', bannerConfig.current.data.ctaUrlNewWindow, bannerConfig.current.data.buttonCtaUrl);
    if (bannerConfig.current.data.ctaUrlNewWindow) {
      setTimeout(() => { window.open(bannerConfig.current.data.buttonCtaUrl, '_blank'); }, 100);
    } else {
      window.location = bannerConfig.current.data.buttonCtaUrl;
    }
  };

  // TODO: creative wishlist item to animate banner dismiss (scroll up out of view) // potentially easier to do on desktop only
  function dismissBanner() {
    setBannerDismissed(true);
    Session.set(DISMISSED_SESSION_KEY, true);
    headerContainer.classList.remove('nmx-informational-banner-active');
  }

  // TODO: has to be a better way to do this
  // doing this because for some reason the height of the bannerEl ref is not correct on the initial load for mobile
  // forcing a re render in this seems to fix the height issue on the initial load for mobile
  const onBannerRefChange = useCallback((node) => {
    setInitialBannerRender(true); // trigger re render to get the correct height of the banner
    bannerEl.current = node;
  }, []);

  return (
    (retrievedBannerConfig && bannerConfig.current.isActive === true && !bannerDismissed) && (
      <InformationalBanner id={'nmx-informational-banner'} className={`nmx-no-print ${bannerId} ${bannerId}__${bannerConfig.current.variant} ${drawerStatus ? 'drawer-is-open' : ''}`} style={{ backgroundColor: bannerConfig.current.bgColor || DEFAULT_BG_COLOR }} ref={onBannerRefChange}>
        <ContainerDefault>
          {bannerConfig.current.variant === 'text-link' && (
            <Row>
              <Col nested>
                <Row>
                  <Col
                    styles={styles.ColInner(bannerConfig.current.startAdornment, bannerConfig.current.hideDismissButton || DEFAULT_HIDE_DISMISS_BUTTON, 'text-link')}
                  >
                    <DataTextContainer variant='text-link'>
                      {bannerConfig.current.startAdornment ? <StartAdornment styles={styles.StartAdornment} variant='text-link'><Icon variant={bannerConfig.current.startAdornment} className="nmx-icon-start-adornment" width='18px' height='18px' style={{ verticalAlign: 'middle' }} /></StartAdornment> : null}
                      <Link
                        id={`${bannerId}-link-id`}
                        variant="secondary"
                        href={bannerConfig.current.data.ctaUrl}
                        aria-label={bannerConfig.current.data.ctaAriaLabel}
                        {...bannerConfig.current.data.ctaUrlNewWindow && { target: '_blank' }}
                      >
                        {bannerConfig.current.data.text}
                      </Link>
                    </DataTextContainer>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          {bannerConfig.current.variant === 'cta-button' && (
            <Row>
              <Col nested>
                <Row>
                  <Col
                    className="nmx-align-center"
                    styles={styles.ColInner(bannerConfig.current.startAdornment, bannerConfig.current.hideDismissButton || DEFAULT_HIDE_DISMISS_BUTTON, 'cta-button')}
                  >
                    <DataTextContainer variant='cta-button'>
                      {bannerConfig.current.startAdornment ? <StartAdornment styles={styles.StartAdornment} variant='cta-button'><Icon variant={bannerConfig.current.startAdornment} className="nmx-icon-start-adornment" width='18px' height='18px' style={{ verticalAlign: 'middle' }} /></StartAdornment> : null}
                      {
                        bannerConfig.current.data.text.length > 0
                        && bannerConfig.current.data.text.map((item, index) => (
                          <Fragment key={`${item}${index}`}>
                            {item}
                            {index < bannerConfig.current.data.text.length - 1 && <DataSeparator>&#124;</DataSeparator>}
                          </Fragment>
                        ))
                      }
                    </DataTextContainer>
                    <Button
                      id={`${bannerId}-cta-button-id`}
                      aria-label="Test Banner CTA Button"
                      variant="tertiary"
                      onClick={handleCtaClick}
                      styles={styles.CtaButton('cta-button', bannerConfig.current.startAdornment)}
                    >
                      {bannerConfig.current.data.buttonCtaText}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          {bannerConfig.current.variant === 'drawer' && (
            <Row>
              <Col nested>
                <Row>
                  <Col
                    styles={styles.ColInner(bannerConfig.current.startAdornment, bannerConfig.current.hideDismissButton || DEFAULT_HIDE_DISMISS_BUTTON, 'drawer')}
                  >
                    <DataTextContainer variant='drawer'>
                      {bannerConfig.current.startAdornment ? <StartAdornment styles={styles.StartAdornment} variant='drawer'><Icon variant={bannerConfig.current.startAdornment} className="nmx-icon-start-adornment" width='18px' height='18px' style={{ verticalAlign: 'middle' }} /></StartAdornment> : null}
                      <Button
                        id={`${bannerId}-cta-expand-button-id`}
                        aria-label="Open Drawer for more information"
                        variant="quarternary"
                        onClick={toggleDrawer}
                        styles={styles.CtaButton('quarternary')}
                      >
                        {bannerConfig.current.data.ctaText}
                        <Icon variant={drawerStatus ? 'drawer-caret-flipped' : 'drawer-caret'} />
                      </Button>
                    </DataTextContainer>
                  </Col>
                </Row>
                {drawerStatus && (
                  <Row>
                    <Col
                      styles={styles.ColDrawer()}
                      className='nmx-informational-banner-drawer__drawer-contents'
                    >
                      <DataTextContainer variant='drawer-contents'>
                        {bannerConfig.current.data.drawerContents.text} <Link variant="secondary" href={bannerConfig.current.data.drawerContents.linkUrl} {...bannerConfig.current.data.drawerContents.ctaUrlNewWindow && { target: '_blank' }}>{bannerConfig.current.data.drawerContents.linkText}</Link>
                      </DataTextContainer>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          )}
        </ContainerDefault>
        {!bannerConfig.current.hideDismissButton && (
          <DismissButton onClick={dismissBanner} aria-label="Dismiss Banner" className='nmx-informational-banner__dismiss-button' />
        )}
      </InformationalBanner>
    )
  );
};

BannerModule.propTypes = {
  'data-banner-id': PropTypes.string,
  'data-static-config': PropTypes.object,
  'data-app-name': PropTypes.string,
  'data-dynamic-config-base-url': PropTypes.string,
};

export default withTheme(BannerModule);
